<template>
  <!-- Who in the pool took each side of one game (utils/poolCrowd.js
       gamePoolView): the whole pool, then the players closest to you, then
       what the result would do (open game) or did (final) to your rank. -->
  <div class="mt-3 border-t border-white/[0.08] pt-3 first:mt-0 first:border-t-0 first:pt-0">
    <div class="flex items-baseline justify-between gap-2">
      <span class="text-[9px] font-extrabold tracking-[0.16em] text-slate-400">THE POOL</span>
      <span v-if="view.available" class="whitespace-nowrap text-[10px] font-bold text-slate-500">{{ view.pool.picked }} of {{ view.pool.field }} picked this game</span>
    </div>

    <p v-if="!view.available" class="mt-1.5 text-[11px] font-semibold leading-snug text-slate-400">
      Who picked what shows up once Sunday's All Picks sheet comes in.
    </p>
    <p v-else-if="!view.pool.picked" class="mt-1.5 text-[11px] font-semibold text-slate-400">Nobody in the pool picked this game.</p>

    <template v-else>
      <div class="mt-2 flex h-6 gap-0.5 overflow-hidden rounded-[7px]" role="img" :aria-label="barLabel">
        <span v-for="s in sides" v-show="s.count" :key="s.side" class="flex min-w-[30px] items-center px-1.5" :class="s.side === order[1] ? 'justify-end' : ''" :style="{ flex: s.count, background: s.fill }">
          <img v-if="s.logo" :src="s.logo" alt="" class="h-4 w-4" :class="{ 'opacity-60': s.lost }" />
        </span>
      </div>
      <div class="mt-1 flex justify-between text-[11px] font-bold tabular-nums text-slate-300">
        <span v-for="s in sides" :key="s.side">
          <b :class="s.nameClass">{{ s.abbr }} {{ s.count }}<template v-if="s.mark"> {{ s.mark }}</template></b> · {{ s.pct }}<template v-if="s.side === order[0]"> of pool</template>
        </span>
      </div>

      <div v-if="view.near" class="mt-2.5 rounded-[10px] bg-white/[0.04] px-2.5 pb-1 pt-2">
        <div class="flex items-baseline justify-between gap-2">
          <span class="text-[9px] font-extrabold tracking-[0.14em] text-slate-400">CLOSEST TO YOU</span>
          <span class="text-[10px] font-bold text-slate-500">{{ view.near.field }} players within 2 wins</span>
        </div>
        <div v-for="s in sides" :key="s.side" class="flex min-h-[26px] items-center gap-2 text-xs font-bold text-slate-200">
          <img v-if="s.logo" :src="s.logo" alt="" class="h-[18px] w-[18px]" />
          <span class="flex-1">{{ view.near[s.side] }} took {{ s.abbr }}</span>
          <span v-if="s.verdict" class="text-[11px] font-extrabold" :class="s.verdictClass">{{ s.verdict }}</span>
        </div>
        <div class="flex min-h-[26px] items-center gap-2 text-xs font-bold text-slate-400">
          <span class="w-[18px]"></span>
          <span>{{ view.near.skipped }} didn't pick it</span>
        </div>
      </div>

      <div v-if="view.moved != null" class="mt-2 flex items-center justify-between gap-2.5 rounded-[10px] border p-2.5" :class="movedTone.box">
        <span class="text-xs font-bold text-slate-200">Your rank from this game</span>
        <span class="whitespace-nowrap text-lg font-black" :class="movedTone.text">{{ movedText }}</span>
      </div>

      <div v-if="view.swing" class="mt-2 grid grid-cols-2 gap-2">
        <div v-for="s in swings" :key="s.side" class="rounded-[10px] border px-2.5 py-2" :class="s.box">
          <div class="text-[10px] font-bold text-slate-400">If {{ s.abbr }} covers</div>
          <div class="whitespace-nowrap text-lg font-black tabular-nums" :class="s.text">
            {{ s.arrow }} <span class="text-[11px] font-bold text-slate-300">you to {{ s.rank }}</span>
          </div>
        </div>
      </div>

      <p v-if="hint" class="mt-2 text-[10px] font-semibold text-slate-500">{{ hint }}</p>
    </template>
  </div>
</template>

<script>
import { teamTint } from "@/utils/teamColors.js";
import { teamLogo } from "@/utils/teamLogo.js";

const VERDICT = { win: "covered", loss: "missed", push: "push" };

function arrow(delta) {
  if (!delta) return "–";
  return `${delta > 0 ? "▲" : "▼"}${Math.abs(delta)}`;
}

export default {
  name: "PoolCrowdSection",
  props: {
    // gamePoolView output.
    view: { type: Object, required: true },
    homeTeam: { type: String, default: null },
    awayTeam: { type: String, default: null },
    // The side the row is read from leads the bar, the list and the tiles.
    first: { type: String, default: "home" },
  },
  computed: {
    order() {
      return this.first === "away" ? ["away", "home"] : ["home", "away"];
    },
    sides() {
      const v = this.view;
      return this.order.map((side) => {
        const result = v.result ? v.result[side] : null;
        const lost = result === "loss";
        const team = side === "home" ? this.homeTeam : this.awayTeam;
        const count = v.pool[side];
        return {
          side,
          abbr: v[side],
          count,
          pct: `${Math.round((count / v.pool.field) * 100)}%`,
          logo: teamLogo(v[side]),
          lost,
          fill: lost ? "rgba(148,163,184,0.18)" : teamTint(team, 0.6),
          mark: result === "win" ? "✓" : lost ? "✕" : null,
          nameClass: result === "win" ? "text-emerald-400" : lost ? "text-rose-400" : "text-white",
          verdict: result ? VERDICT[result] : null,
          verdictClass: result === "win" ? "text-emerald-400" : lost ? "text-rose-400" : "text-slate-400",
        };
      });
    },
    barLabel() {
      return this.sides.map((s) => `${s.count} picked ${s.abbr}`).join(", ");
    },
    movedText() {
      const moved = this.view.moved;
      if (!moved) return "No change";
      return `${moved > 0 ? "▲" : "▼"}${Math.abs(moved)} ${Math.abs(moved) === 1 ? "spot" : "spots"}`;
    },
    movedTone() {
      const moved = this.view.moved;
      if (moved > 0) return { box: "border-emerald-400/30 bg-emerald-400/[0.07]", text: "text-emerald-400" };
      if (moved < 0) return { box: "border-rose-400/25 bg-rose-400/[0.06]", text: "text-rose-400" };
      return { box: "border-white/10 bg-white/[0.03]", text: "text-slate-300" };
    },
    swings() {
      return this.order.map((side) => {
        const { rank, delta } = this.view.swing[side];
        const tone = delta > 0
          ? { box: "border-emerald-400/30 bg-emerald-400/[0.07]", text: "text-emerald-400" }
          : delta < 0
            ? { box: "border-rose-400/25 bg-rose-400/[0.06]", text: "text-rose-400" }
            : { box: "border-white/10 bg-white/[0.03]", text: "text-slate-300" };
        return { side, abbr: this.view[side], rank, arrow: arrow(delta), ...tone };
      });
    },
    // One line of plain advice for an open game you are not on.
    hint() {
      const v = this.view;
      if (!v.swing || !v.near) return null;
      if (v.mine) return null;
      if (v.near.home === v.near.away) return "Not your game: it only moves the people around you.";
      const rootFor = v.near.home > v.near.away ? v.away : v.home;
      const theirs = rootFor === v.home ? v.away : v.home;
      return `Root for ${rootFor}: more of the people around you took ${theirs}.`;
    },
  },
};
</script>
